import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import { openModalAction } from '../../actions/modal';
import { isCanceledResponse } from '../../middleware/api';
import { DATE_MONTH_DAY_YEAR } from '../../constants';
import Strings from '../../Strings';
import { actions, errorMessage, LIST_PATIENT_SURVEYS_RESULT } from './actions';
import styles from './Surveys.module.scss';

const formatDate = value => (value ? moment(value).format(DATE_MONTH_DAY_YEAR) : '-');

// Responses that have answers worth opening in the read-only view
const VIEWABLE = ['completed', 'in_progress'];

// One survey of the patient: a row per question, a column per send (oldest first), so answers
// can be compared over time.
function SurveyComparison({ survey, onView }) {
  return (
    <section className={styles.patientSurvey} data-testid={`patient-survey[${survey.id}]`}>
      <h3 className={styles.sectionTitle}>
        {survey.title} {survey.archived && <small className={styles.muted}>{Strings.surveys.archivedSuffix}</small>}
      </h3>
      <div className={styles.compareScroll}>
        <table className={styles.compareTable}>
          <thead>
            <tr>
              <th>{Strings.surveys.question}</th>
              {survey.responses.map((response, index) => (
                <th key={response.id} data-testid={`patient-survey-response[${index}]`}>
                  <div className={styles.compareTimepoint}>{response.timepoint || formatDate(response.created_at)}</div>
                  <div className={styles.muted}>
                    {`${Strings.surveys.assignmentStatus[response.status] || response.status} · ${formatDate(
                      response.completed_at || response.sent_at || response.created_at,
                    )} · v${response.version}`}
                  </div>
                  {VIEWABLE.includes(response.status) && (
                    <button type="button" className="white link" onClick={() => onView(survey.id, response.id)}>
                      {Strings.surveys.viewResponse}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {survey.questions.map(question => (
              <tr key={question.link_id} data-testid={`patient-survey-question[${question.link_id}]`}>
                <th scope="row">{question.text}</th>
                {survey.responses.map(response => (
                  <td key={response.id}>{response.answers[question.link_id] ?? Strings.surveys.notAnswered}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

SurveyComparison.propTypes = {
  survey: PropTypes.object.isRequired,
  onView: PropTypes.func.isRequired,
};

function PatientSurveys({ patientUuid, refreshKey, listPatientSurveys, openModal }) {
  const [surveys, setSurveys] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientUuid) return;
    listPatientSurveys(patientUuid).then(resp => {
      if (isCanceledResponse(resp)) return;
      if (resp?.type === LIST_PATIENT_SURVEYS_RESULT) {
        setSurveys(resp.response.surveys);
        setError(null);
      } else {
        setError(errorMessage(resp, Strings.surveys.loadFailed));
      }
    });
  }, [patientUuid, refreshKey, listPatientSurveys]);

  const onView = (surveyId, assignmentId) => openModal('survey-response-modal', { surveyId, assignmentId });

  if (error) return <div className={styles.patientSurveys}>{error}</div>;
  if (!surveys) return null;
  return (
    <div className={styles.patientSurveys} data-testid="patient-surveys">
      {surveys.length === 0 && <div className={styles.muted}>{Strings.surveys.noPatientSurveys}</div>}
      {surveys.map(survey => (
        <SurveyComparison key={survey.id} survey={survey} onView={onView} />
      ))}
    </div>
  );
}

PatientSurveys.propTypes = {
  patientUuid: PropTypes.string,
  refreshKey: PropTypes.number,
  listPatientSurveys: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  listPatientSurveys: patientUuid => dispatch(actions.listPatientSurveys(patientUuid)),
  openModal: (name, data) => dispatch(openModalAction(name, data)),
});

export default connect(null, mapDispatchToProps)(PatientSurveys);
